import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import Button from '../components/ui/Button';

export default function PaymentHistory() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await api('/api/payment-history');
      console.log('Payment history response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response));
      
      if (response && response.ok && response.data) {
        console.log('Payments:', response.data.payments);
        setPayments(response.data.payments || []);
      } else {
        console.error('Invalid response format:', response);
        setError(response?.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Failed to fetch payment history:', err);
      console.error('Error details:', err.message);
      setError(`Failed to load payment history: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadReceipt = async (paymentId, format = 'html') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/receipt/${paymentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download receipt');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      if (format === 'pdf') {
        // Open in new window for PDF printing
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 250);
          };
        }
      } else {
        // Download as HTML file
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sitescope-receipt-${paymentId}.html`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      // Cleanup after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download receipt:', err);
      alert('Failed to download receipt');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <span className="material-icons text-emerald-400">check_circle</span>;
      case 'failed':
        return <span className="material-icons text-red-400">cancel</span>;
      default:
        return <span className="material-icons text-amber-400">schedule</span>;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      succeeded: 'bg-emerald-500/10 text-emerald-400',
      failed: 'bg-red-500/10 text-red-400',
      pending: 'bg-amber-500/10 text-amber-400'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070b14] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500/20 border-t-emerald-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#070b14]">
        <header className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="SiteScope" className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="text-xl sm:text-2xl font-bold text-white">SiteScope</span>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070b14]">
      {/* Header matching Landing/Pricing pages */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <img src="/logo-icon.png" alt="SiteScope" className="w-8 h-8 sm:w-10 sm:h-10" />
          <span className="text-xl sm:text-2xl font-bold text-white">SiteScope</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Button as={Link} to="/dashboard" variant="outline" size="sm" className="text-sm sm:text-base px-3 sm:px-4">
            Dashboard
          </Button>
          <Button as={Link} to="/profile" size="sm" className="text-sm sm:text-base px-3 sm:px-4">
            Profile
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <span className="material-icons text-xl">arrow_back</span>
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
            <span className="material-icons text-emerald-400" style={{ fontSize: '2rem' }}>credit_card</span>
            Payment History
          </h1>
          <p className="mt-2 text-gray-400">View and download receipts for your transactions</p>
        </div>

      {payments.length === 0 ? (
        <div className="bg-[#0b1120] rounded-2xl border border-white/5 p-12 text-center">
          <span className="material-icons text-gray-400 mx-auto mb-4" style={{ fontSize: '4rem' }}>credit_card</span>
          <h3 className="text-xl font-semibold text-white mb-2">No payment history</h3>
          <p className="text-gray-400">You haven't made any payments yet.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-[#0b1120] rounded-2xl border border-white/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/5">
                <thead className="bg-white/[0.02]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receipt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-white capitalize">
                          {payment.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white">
                        ₱{(payment.amount / 100).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(payment.status)}
                          {getStatusBadge(payment.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs font-mono text-gray-500">
                          {payment.payment_intent_id.substring(0, 20)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.status === 'succeeded' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => downloadReceipt(payment.id, 'html')}
                              className="flex items-center gap-1 px-3 py-2 bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 rounded-lg hover:bg-cyan-500/25 transition-colors text-sm font-medium"
                              title="Download as HTML"
                            >
                              <span className="material-icons text-sm">html</span>
                              HTML
                            </button>
                            <button
                              onClick={() => downloadReceipt(payment.id, 'pdf')}
                              className="flex items-center gap-1 px-3 py-2 bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/25 transition-colors text-sm font-medium"
                              title="Print to PDF"
                            >
                              <span className="material-icons text-sm">picture_as_pdf</span>
                              PDF
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {payments.map((payment) => (
              <div key={payment.id} className="bg-[#0b1120] rounded-2xl p-4 border border-white/5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Plan</p>
                    <p className="text-lg font-bold text-white capitalize">{payment.plan}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-lg font-bold text-white">₱{(payment.amount / 100).toFixed(2)}</p>
                  </div>
                </div>

                <div className="mb-3 pb-3 border-b border-white/5">
                  <p className="text-xs text-gray-500 mb-1">Date</p>
                  <p className="text-sm text-white">{formatDate(payment.created_at)}</p>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  {getStatusIcon(payment.status)}
                  {getStatusBadge(payment.status)}
                </div>

                <div className="mb-3 pb-3 border-b border-white/5">
                  <p className="text-xs text-gray-500 mb-1">Payment ID</p>
                  <p className="text-xs font-mono text-gray-500 break-all">
                    {payment.payment_intent_id}
                  </p>
                </div>

                {payment.status === 'succeeded' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadReceipt(payment.id, 'html')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 rounded-lg hover:bg-cyan-500/25 transition-colors font-medium"
                      title="Download as HTML"
                    >
                      <span className="material-icons text-base">html</span>
                      HTML
                    </button>
                    <button
                      onClick={() => downloadReceipt(payment.id, 'pdf')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/25 transition-colors font-medium"
                      title="Print to PDF"
                    >
                      <span className="material-icons text-base">picture_as_pdf</span>
                      PDF
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {payments.length > 0 && (
        <div className="mt-6 bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
          <p className="text-sm text-gray-300">
            <strong>💡 Download Options:</strong><br/>
            • <strong>HTML:</strong> Download receipt as HTML file to view in any browser<br/>
            • <strong>PDF:</strong> Opens receipt in new tab with print dialog - choose "Save as PDF" as your printer destination to save locally
          </p>
        </div>
      )}
      </main>
    </div>
  );
}
